import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";

import { TableHead } from "@/components/ui/table";
import type { SortDirection, SortState } from "@/lib/tableSort";
import { cn } from "@/lib/utils";

export interface SortableHeaderProps<ColumnKey extends string> {
  /** The column this header drives. Compared against `sort.column`. */
  column: ColumnKey;
  /** Visible header text. */
  children: React.ReactNode;
  /** The ledger's current sort state (from `useTableSort`). */
  sort: SortState<ColumnKey>;
  /** Advance the tri-state cycle for this column (from `useTableSort`). */
  onToggle: (column: ColumnKey) => void;
  /** Right-align the label (numeric/currency columns, matching the cells). */
  align?: "left" | "right";
  className?: string;
}

/** aria-sort token for the active column; "none" once unsorted (CHP-417 a11y). */
function ariaSort(direction: SortDirection): "ascending" | "descending" | "none" {
  if (direction === "asc") return "ascending";
  if (direction === "desc") return "descending";
  return "none";
}

/**
 * A sortable column header for the data ledgers (CHP-417).
 *
 * Renders a `<th>` whose label is a button: first click sorts ascending,
 * second toggles descending, a third resets to the default order. The arrow
 * indicator is persistent on the active column (up = asc, down = desc) and
 * appears on hover/focus for inactive columns (the muted up/down chevron),
 * signalling that the column is sortable. The same component is used across
 * every ledger so the interaction is identical (Consistency criterion).
 */
export function SortableHeader<ColumnKey extends string>({
  column,
  children,
  sort,
  onToggle,
  align = "left",
  className,
}: SortableHeaderProps<ColumnKey>) {
  const isActive = sort.column === column;
  const direction = isActive ? sort.direction : null;

  return (
    <TableHead
      scope="col"
      aria-sort={isActive ? ariaSort(direction) : "none"}
      className={cn("p-0", className)}
    >
      <button
        type="button"
        onClick={() => onToggle(column)}
        data-testid={`sort-header-${column}`}
        data-sort-direction={direction ?? "none"}
        className={cn(
          "group flex h-9 w-full items-center gap-1 px-3 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          align === "right" ? "justify-end" : "justify-start",
          isActive && "text-foreground",
        )}
      >
        {children}
        {isActive ? (
          direction === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 text-primary" aria-hidden />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 text-primary" aria-hidden />
          )
        ) : (
          <ChevronsUpDown
            className="h-3.5 w-3.5 text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            aria-hidden
          />
        )}
      </button>
    </TableHead>
  );
}
